/**
 * Configuration HTTP partagee entre tous les modules FrontEnd.
 * Permet a apiClient de fonctionner independamment de tout module specifique.
 *
 * L'initialisation se fait au demarrage de l'app (main.tsx ou configureAuthModule).
 */

export interface HttpClientConfig {
  /** URL de base de l'API backend */
  apiBaseUrl: string;

  /** Configuration des tokens JWT */
  tokens: {
    autoRefresh: boolean;
    /** Marge avant expiration pour declencher le refresh (millisecondes) */
    refreshMargin: number;
  };

  /** Configuration du comportement d'authentification */
  auth: {
    /** Endpoint pour rafraichir le token */
    refreshEndpoint: string;
    /** URL de redirection quand non authentifie */
    loginUrl: string;
    /**
     * Callback appele lors d'un 401 (non autorise).
     * Doit nettoyer l'etat d'authentification et rediriger si necessaire.
     */
    onUnauthorized: () => void;
  };
}

/**
 * Configuration par defaut.
 * Sera surchargee par configureHttpClient() au demarrage de l'app.
 */
const defaultConfig: HttpClientConfig = {
  apiBaseUrl: '',
  tokens: {
    autoRefresh: true,
    refreshMargin: 5 * 60 * 1000, // 5 minutes
  },
  auth: {
    refreshEndpoint: '/api/auth/refresh',
    loginUrl: '/login',
    onUnauthorized: () => {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    },
  },
};

let currentConfig: HttpClientConfig = { ...defaultConfig };

/**
 * Configure le client HTTP partage.
 * Doit etre appele au demarrage de l'app (avant toute requete API).
 */
export function configureHttpClient(config: Partial<HttpClientConfig>): void {
  currentConfig = {
    ...currentConfig,
    ...config,
    tokens: {
      ...currentConfig.tokens,
      ...config.tokens,
    },
    auth: {
      ...currentConfig.auth,
      ...config.auth,
    },
  };
}

/**
 * Recupere la configuration HTTP actuelle.
 */
export function getHttpConfig(): HttpClientConfig {
  return currentConfig;
}

/**
 * Reinitialise la configuration (utile pour les tests).
 */
export function resetHttpConfig(): void {
  currentConfig = { ...defaultConfig };
}
